import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import usePageMeta from "../hooks/usePageMeta";
import Footer from "../components/Footer";
import ContactCTA from "../components/ContactCTA";
import { Phone, CheckCircle, Shield, ShieldCheck, Bug, Laptop, Bot, Brain, ArrowRight, ArrowLeft } from "lucide-react";

// Hook for fade-in on scroll (preserved)
function useScrollFadeIn() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, []);

  return { ref, isVisible };
}

// SkyGlobal AI Product Item Component
function AIPeopleCountingItem({ item, index, t }) {
  const { ref, isVisible } = useScrollFadeIn();

  return (
    <div
      ref={ref}
      className={`${index > 0 ? "bg-surface" : "bg-white"} transition-[opacity,transform] duration-700 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="container-page py-24 tablet:py-16">
        <div className="grid items-center gap-16 tablet:gap-10 grid-cols-2 mobile:grid-cols-1">
          {item.imagePosition === "left" && (
            <div className="relative overflow-hidden rounded-2xl border border-line bg-surface p-8 tablet:p-6 mobile:p-4 group">
              <img
                alt={t(`products.skyglobalAi.items.${item.key}.title`)}
                width="600"
                height="400"
                className="w-full rounded-lg shadow-2xl transition-transform duration-500 group-hover:scale-105"
                src={item.image}
              />
              <div className="absolute right-6 top-6 mobile:right-4 mobile:top-4 rounded-full bg-textBlue px-4 py-2 text-xs font-bold text-white uppercase tracking-widest">
                {item.badge}
              </div>
            </div>
          )}

          <div>
            <h2 className="text-4xl tablet:text-3xl mobile:text-2xl font-semibold tracking-[-0.02em] text-textBlue">
              {t(`products.skyglobalAi.items.${item.key}.title`)}
            </h2>
            <p className="mt-6 mobile:mt-4 text-lg tablet:text-base text-gray-600 leading-relaxed">
              {t(`products.skyglobalAi.items.${item.key}.description`)}
            </p>
            <div className="mt-8 mobile:mt-6 grid gap-x-4 gap-y-3 grid-cols-2 mobile:grid-cols-1">
              {t(`products.skyglobalAi.items.${item.key}.features`, { returnObjects: true }).map((feature, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 p-2 -m-2 rounded-lg transition-[background-color,transform] duration-300 hover:bg-brand-50 hover:translate-x-1"
                >
                  <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-600" aria-hidden="true" />
                  <span className="text-sm font-medium text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
            <a
              href={item.pdf}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-10 mobile:mt-8 rounded-full px-7 py-3 text-sm font-semibold bg-textBlue text-white transition-colors hover:bg-textBlueDark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-textBlue focus-visible:ring-offset-2"
            >
              {t("products.skyglobalAi.downloadDataSheet")}
            </a>
          </div>

          {item.imagePosition === "right" && (
            <div className="relative overflow-hidden rounded-2xl border border-line bg-surface p-8 tablet:p-6 mobile:p-4 group">
              <img
                alt={t(`products.skyglobalAi.items.${item.key}.title`)}
                width="600"
                height="400"
                className="w-full rounded-lg shadow-2xl transition-transform duration-500 group-hover:scale-105"
                src={item.image}
              />
              <div className="absolute right-6 top-6 mobile:right-4 mobile:top-4 rounded-full bg-textBlue px-4 py-2 text-xs font-bold text-white uppercase tracking-widest">
                {item.badge}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const products = [
  { icon: Brain, key: "skyglobalAi", color: "text-purple-600", bgColor: "bg-purple-50" },
  { icon: Shield, key: "secure", color: "text-red-600", bgColor: "bg-red-50" },
  { icon: Phone, key: "smartLiving", color: "text-textBlue", bgColor: "bg-blueLight" },
];

const smartLivingProducts = [
  { key: "skydial", logo: "/logo skydial.svg", isImage: true },
  { key: "telephony", logo: "/telephony.png", isImage: true },
  { key: "grandstream", logo: "/GRANDSTREAM.png", isImage: true, isWide: true },
  { key: "yeastar", logo: "/yeastar.png", isImage: true },
  { key: "voiceapi", logo: "/Voice API.png", isImage: true },
  { key: "aichatbot", icon: Bot, isIcon: true },
];

const secureProducts = [
  { key: "soc", solutionKey: "soc", icon: ShieldCheck, color: "text-red-600", bgColor: "bg-red-50" },
  { key: "vapt", solutionKey: "vapt", icon: Bug, color: "text-amber-600", bgColor: "bg-amber-50" },
  { key: "firstprotect", solutionKey: "firstprotect", icon: Laptop, color: "text-indigo-600", bgColor: "bg-indigo-50" },
];

const skyglobalAiProducts = [
  { key: "ccd21", image: "/CCD21.png", badge: "AI Vision", imagePosition: "left", pdf: "/CCD21.pdf" },
  { key: "fp221", image: "/FP221.png", badge: "TOF", imagePosition: "right", pdf: "/FP221.pdf" },
];

export default function ProductDetail() {
  const { t } = useTranslation();
  const { productKey } = useParams();

  const product = products.find((p) => p.key === productKey);

  usePageMeta({
    title: product ? `${t(`products.${product.key}.title`)} | Sky Global` : "Products | Sky Global",
    description: product ? t(`products.${product.key}.description`) : t("products.title"),
    path: `/products/${productKey}`,
  });

  if (!product) {
    return (
      <>
        <div className="pt-40 min-h-[60vh] flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-black mb-4">{t("notFound.unknownProduct")}</h1>
            <Link
              to="/products"
              className="text-textBlue hover:text-textBlueDark font-semibold no-underline transition-colors"
            >
              {t("notFound.backProducts")}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const Icon = product.icon;

  return (
    <>
      <div className="bg-white">
        {/* Header */}
        <section className="relative overflow-hidden page-header pt-40 tablet:pt-36 mobile:pt-32 pb-20 tablet:pb-16 px-6 mobile:px-4">
          <div
            className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,#000_25%,transparent_70%)]"
            aria-hidden="true"
          ></div>
          <div className="relative container-page">
            <Link
              to="/products"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-textBlue hover:text-textBlueDark no-underline mb-6"
            >
              <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              {t("products.label")}
            </Link>
            <div className="mb-5">
              <span className={`${product.bgColor} w-12 h-12 rounded-xl flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${product.color}`} aria-hidden="true" />
              </span>
            </div>
            <h1 className="text-5xl tablet:text-4xl mobile:text-3xl font-semibold tracking-[-0.02em] text-black leading-[1.05] max-w-3xl mb-3">
              {t(`products.${product.key}.title`)}
            </h1>
            {t(`products.${product.key}.subtitle`, { defaultValue: "" }) && (
              <p className="text-lg tablet:text-base text-gray-700 leading-relaxed max-w-2xl">
                {t(`products.${product.key}.subtitle`)}
              </p>
            )}
          </div>
        </section>

        {/* Smart Living Products Section */}
        {product.key === "smartLiving" && (
          <section className="container-page py-24 tablet:py-20">
            <h2 className="sr-only">{t("products.smartLiving.title")}</h2>
            <div className="grid gap-6 grid-cols-3 desktop:grid-cols-4 tablet:grid-cols-2 mobile:grid-cols-1">
              {smartLivingProducts.map((item) => {
                const CardWrapper = item.url ? "a" : "div";
                const ItemIcon = item.icon;
                const cardProps = item.url
                  ? { href: item.url, target: "_blank", rel: "noopener noreferrer" }
                  : {};

                return (
                  <CardWrapper
                    key={item.key}
                    {...cardProps}
                    className={`group flex flex-col rounded-xl border border-line bg-white p-6 transition-[border-color,box-shadow] duration-300 hover:border-textBlue/30 hover:shadow-lift ${
                      item.url ? "cursor-pointer" : ""
                    }`}
                  >
                    <div
                      className={`mb-4 flex items-center justify-center rounded-lg bg-blueLight h-12 ${
                        item.isWide ? "w-32 px-2" : "w-12"
                      }`}
                    >
                      {item.isIcon ? (
                        <ItemIcon className="h-6 w-6 text-textBlue" aria-hidden="true" />
                      ) : (
                        <img
                          alt={t(`products.smartLiving.items.${item.key}.title`)}
                          width="96"
                          height="40"
                          className={`object-contain ${item.isWide ? "w-full h-6" : "h-10 w-10"}`}
                          src={item.logo}
                        />
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-black">
                      {t(`products.smartLiving.items.${item.key}.title`)}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                      {t(`products.smartLiving.items.${item.key}.description`)}
                    </p>
                    {item.key === "skydial" && (
                      <Link
                        to="/solutions/skydial"
                        className="mt-3 inline-flex items-center gap-1 text-sm text-textBlue hover:text-textBlueDark font-semibold transition-colors no-underline"
                      >
                        {t("products.viewMore")}
                      </Link>
                    )}
                  </CardWrapper>
                );
              })}
            </div>
          </section>
        )}

        {/* Secure Products Section */}
        {product.key === "secure" && (
          <section className="container-page py-24 tablet:py-20">
            <h2 className="sr-only">{t("products.secure.title")}</h2>
            <div className="grid gap-6 grid-cols-3 desktop:grid-cols-4 tablet:grid-cols-2 mobile:grid-cols-1">
              {secureProducts.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <div
                    key={item.key}
                    className="group flex flex-col rounded-xl border border-line bg-white p-6 transition-[border-color,box-shadow] duration-300 hover:border-textBlue/30 hover:shadow-lift"
                  >
                    <div className={`mb-4 flex items-center justify-center rounded-lg h-12 w-12 ${item.bgColor}`}>
                      <ItemIcon className={`h-6 w-6 ${item.color}`} aria-hidden="true" />
                    </div>
                    <h3 className="text-lg font-semibold text-black">
                      {t(`products.secure.items.${item.key}.title`)}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500 leading-relaxed">
                      {t(`products.secure.items.${item.key}.description`)}
                    </p>
                    <Link
                      to={`/solutions/${item.solutionKey}`}
                      className="mt-3 inline-flex items-center gap-1 text-sm text-textBlue hover:text-textBlueDark font-semibold transition-colors no-underline"
                    >
                      {t("products.viewMore")}
                      <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* SkyGlobal AI Products Section */}
        {product.key === "skyglobalAi" && (
          <section>
            {skyglobalAiProducts.map((item, index) => (
              <AIPeopleCountingItem key={item.key} item={item} index={index} t={t} />
            ))}
          </section>
        )}

        <ContactCTA />
        <Footer />
      </div>
    </>
  );
}
